/**
 * Drawer "Acessos" — abre dentro de UsersPage ao clicar no botão Acessos
 * de uma linha. Mostra, por instituição que o usuário pertence:
 *   - Roles efetivas (chip com origem direct/group)
 *   - Grupos atribuídos (chips removíveis)
 *   - Botão para acrescentar Role direta (autocomplete entre Roles de módulos
 *     com licença ativa naquela instituição)
 *   - Botão para acrescentar Grupo (autocomplete entre RoleGroups global ou
 *     daquela instituição)
 *
 * Toda mutação invalida 'user-access' para re-fetch automático.
 * Endpoints consumidos:
 *   GET    /api/users/:id/access            (lista membership + effective)
 *   GET    /api/roles?module_id=<uuid>      (catalogo de Roles por módulo)
 *   GET    /api/role-groups?scope=...       (grupos disponíveis)
 *   POST   /api/roles/memberships/:m/roles/:r
 *   DELETE /api/roles/memberships/:m/roles/:r
 *   POST   /api/role-groups/memberships/:m/groups/:g
 *   DELETE /api/role-groups/memberships/:m/groups/:g
 */

import { useState } from "react";
import {
  Drawer, Box, Typography, Stack, Chip, Button, IconButton, Tooltip,
  Autocomplete, TextField, Paper, Divider, CircularProgress, Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";

export interface EffectiveRole {
  id: string; key: string; name: string;
  module: { id: string; key: string; name: string };
  source: "direct" | "group";
  groupId?: string; groupName?: string;
}

export interface AccessGroup {
  id: string; key: string; name: string; roleCount: number;
}

export interface AccessMembership {
  membershipId: string;
  institution: { id: string; name: string; cnpj: string; status: string };
  active: boolean;
  isAdmin: boolean;
  directRoleIds: string[];
  groups: AccessGroup[];
  effectiveRoles: EffectiveRole[];
}

export interface AccessResponse {
  id: string; name: string; email: string; cpf?: string;
  isMasterAdmin: boolean; status: string;
  memberships: AccessMembership[];
}

interface RoleCatalogItem {
  id: string; key: string; name: string;
  module: { id: string; key: string; name: string };
}

interface GroupCatalogItem {
  id: string; key: string; name: string;
  institutionId: string | null;
  roleCount: number;
}

interface Props {
  open: boolean;
  userId: string | null;
  userName?: string;
  onClose: () => void;
}

export function AccessDrawer({ open, userId, userName, onClose }: Props) {
  const qc = useQueryClient();

  const { data, isFetching, error } = useQuery({
    queryKey: ["user-access", userId],
    queryFn: async () => (await api.get<AccessResponse>(`/users/${userId}/access`)).data,
    enabled: open && !!userId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["user-access", userId] });

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: 560 } } }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Acessos · {userName ?? "—"}</Typography>
        <IconButton onClick={onClose} aria-label="Fechar" autoFocus><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        {isFetching && <CircularProgress size={24} />}
        {error && <Alert severity="error">Falha ao carregar acessos.</Alert>}
        {data && data.memberships.length === 0 && (
          <Alert severity="info">Este usuário não tem instituição vinculada.</Alert>
        )}
        <Stack spacing={2}>
          {data?.memberships.map((m) => (
            <MembershipCard key={m.membershipId} m={m} onChanged={invalidate} />
          ))}
        </Stack>
      </Box>
    </Drawer>
  );
}

export function MembershipCard({ m, onChanged, editable = true }: { m: AccessMembership; onChanged: () => void; editable?: boolean }) {
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [addGroupOpen, setAddGroupOpen] = useState(false);

  // Catálogo de Roles disponíveis para esta instituição (todos os módulos com
  // licença ativa — versão simples lista TODOS por enquanto; refinar quando a
  // API expor o filtro por institution+license).
  const rolesQ = useQuery<RoleCatalogItem[]>({
    queryKey: ["roles-all"],
    queryFn: async () => {
      const mods = (await api.get<{ id: string; active?: boolean }[]>("/modules")).data ?? [];
      const all: RoleCatalogItem[] = [];
      for (const md of mods.filter((m) => m.active !== false)) {
        const rs = await api.get<RoleCatalogItem[]>("/roles", { params: { module_id: md.id } });
        all.push(...rs.data);
      }
      return all;
    },
    enabled: addRoleOpen,
  });

  // Grupos: globais (institutionId=null) + os desta institution
  const groupsQ = useQuery<GroupCatalogItem[]>({
    queryKey: ["groups-for-inst", m.institution.id],
    queryFn: async () => {
      const [global, local] = await Promise.all([
        api.get<{ data: GroupCatalogItem[] }>("/role-groups", { params: { scope: "global", page_size: 100 } }),
        api.get<{ data: GroupCatalogItem[] }>("/role-groups", { params: { scope: m.institution.id, page_size: 100 } }),
      ]);
      return [...global.data.data, ...local.data.data];
    },
    enabled: addGroupOpen,
  });

  const assignRole = useMutation({
    mutationFn: (roleId: string) => api.post(`/roles/memberships/${m.membershipId}/roles/${roleId}`),
    onSuccess: () => { setAddRoleOpen(false); onChanged(); },
  });
  const unassignRole = useMutation({
    mutationFn: (roleId: string) => api.delete(`/roles/memberships/${m.membershipId}/roles/${roleId}`),
    onSuccess: onChanged,
  });
  const assignGroup = useMutation({
    mutationFn: (groupId: string) => api.post(`/role-groups/memberships/${m.membershipId}/groups/${groupId}`),
    onSuccess: () => { setAddGroupOpen(false); onChanged(); },
  });
  const unassignGroup = useMutation({
    mutationFn: (groupId: string) => api.delete(`/role-groups/memberships/${m.membershipId}/groups/${groupId}`),
    onSuccess: onChanged,
  });

  const directIds = new Set(m.directRoleIds);

  // Filtra do catálogo as Roles que ainda não estão atribuídas diretamente.
  const availableRoles = (rolesQ.data ?? []).filter((r) => !directIds.has(r.id));
  const assignedGroupIds = new Set(m.groups.map((g) => g.id));
  const availableGroups = (groupsQ.data ?? []).filter((g) => !assignedGroupIds.has(g.id));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <BusinessIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>{m.institution.name}</Typography>
        {!m.active && <Chip size="small" label="inativo" color="warning" />}
        {m.isAdmin && <Chip size="small" label="admin local" color="info" />}
      </Stack>

      <Typography variant="caption" color="text.secondary">Roles efetivas ({m.effectiveRoles.length}):</Typography>
      <Stack direction="row" flexWrap="wrap" spacing={0.5} sx={{ mt: 0.5, mb: 1 }}>
        {m.effectiveRoles.length === 0 && (
          <Typography variant="body2" color="text.secondary">Nenhuma role atribuída.</Typography>
        )}
        {m.effectiveRoles.map((r) => (
          <Tooltip key={r.id + r.source}
            title={r.source === "direct" ? "Atribuída diretamente" : `Vinda do grupo "${r.groupName}"`}>
            <Chip
              size="small"
              label={`${r.module.key} / ${r.name}`}
              color={r.source === "direct" ? "primary" : "default"}
              variant={r.source === "direct" ? "filled" : "outlined"}
              onDelete={editable && r.source === "direct" ? () => unassignRole.mutate(r.id) : undefined}
            />
          </Tooltip>
        ))}
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Typography variant="caption" color="text.secondary">Grupos atribuídos ({m.groups.length}):</Typography>
      <Stack direction="row" flexWrap="wrap" spacing={0.5} sx={{ mt: 0.5, mb: 1 }}>
        {m.groups.length === 0 && (
          <Typography variant="body2" color="text.secondary">Nenhum grupo.</Typography>
        )}
        {m.groups.map((g) => (
          <Chip
            key={g.id} size="small" color="secondary"
            label={`${g.name} (${g.roleCount} roles)`}
            onDelete={editable ? () => unassignGroup.mutate(g.id) : undefined}
          />
        ))}
      </Stack>

      {editable && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => setAddRoleOpen((x) => !x)}>
            Role direta
          </Button>
          <Button size="small" startIcon={<AddIcon />} variant="outlined" color="secondary" onClick={() => setAddGroupOpen((x) => !x)}>
            Grupo
          </Button>
        </Stack>
      )}

      {addRoleOpen && (
        <Box sx={{ mt: 2 }}>
          <Autocomplete<RoleCatalogItem>
            options={availableRoles}
            loading={rolesQ.isFetching}
            getOptionLabel={(o) => `${o.module.key} / ${o.name}`}
            groupBy={(o) => o.module.name}
            onChange={(_, v) => v && assignRole.mutate(v.id)}
            renderInput={(p) => <TextField {...p} label="Adicionar role" size="small" autoFocus />}
            disabled={assignRole.isPending}
          />
        </Box>
      )}

      {addGroupOpen && (
        <Box sx={{ mt: 2 }}>
          <Autocomplete<GroupCatalogItem>
            options={availableGroups}
            loading={groupsQ.isFetching}
            getOptionLabel={(o) => `${o.name}${o.institutionId ? "" : " (global)"} · ${o.roleCount} roles`}
            onChange={(_, v) => v && assignGroup.mutate(v.id)}
            renderInput={(p) => <TextField {...p} label="Adicionar grupo" size="small" autoFocus />}
            disabled={assignGroup.isPending}
          />
        </Box>
      )}
    </Paper>
  );
}
